import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { PortalMessageSearchResultItem } from "../../models/PortalMessageSearchResultItem";
import { CustomDialogSettings } from "src/app/shared/model/CustomDialogSettings";
import { ComposePortalMessageComponent } from "../compose-portal-message/compose-portal-message.component";
import { TranslateService } from "@ngx-translate/core";
import { DialogResult } from "@progress/kendo-angular-dialog";
import { CustomDialogService } from "src/app/shared/services/custom-dialog.service";
import { PortalMessageRecipient } from "../../models/PortalMessageRecipient";
import { ApplicationPortalType } from "src/app/shared/model/ApplicationPortalType";
import { AuthService } from "src/app/core/authentication/auth.service";
import { ConfirmReadingByRecipientData } from "../../models/ConfirmReadingByRecipientData";
import { PortalMessageService } from "../../service/portal-message.service";
import { UserMessagesIndicationService } from "../../service/user-messages-indication.service";
import { UiNotificationService } from "src/app/external-ui-shared/services/ui-notification.service";

@Component({
  selector: "app-message-item",
  templateUrl: "./message-item.component.html",
  styleUrls: ["./message-item.component.scss"],
})
export class MessageItemComponent implements OnInit {
  constructor(
    private translateService: TranslateService,
    private customDialogService: CustomDialogService,
    private messageService: PortalMessageService,
    private userMessagesIndicationService : UserMessagesIndicationService,
    private uiNotification : UiNotificationService,
    private authService: AuthService
  ) {}

  @Input() message: PortalMessageSearchResultItem;
  @Input() userDisplayName: string;
  @Input() applicationPortalTypeId: number;
  @Output() threadChangeEvent = new EventEmitter<string>();

  currentUserId: string;
  isMyMessage: boolean;
  messageAlign: string;
  readConfirmationEnabled: boolean;
  selfRecipient: PortalMessageRecipient;

  ngOnInit(): void {
    this.isMyMessage = this.message.createdBy == this.userDisplayName;
    this.messageAlign = this.isMyMessage ? "end" : "start";
    this.currentUserId = this.authService.getLoggedUserInfo().userData.id;
     

    this.selfRecipient = this.message.recipients.find(r=> r.portalUserId == this.currentUserId);
    if(this.selfRecipient){
      this.readConfirmationEnabled = this.selfRecipient.isReadConfirmationRequired && !this.selfRecipient.readConfirmationSent;
    }

  }

  @ViewChild("composeReplyPortalMessageDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  reply() {
    const recipients: PortalMessageRecipient[] = [];
    recipients.push(this.getRecipientFromSender());
    this.replyMessage(recipients);
  }

  replyAll() {
    const recipients: PortalMessageRecipient[] = [];
    recipients.push(this.getRecipientFromSender());

    this.message.recipients.forEach((r) => {
      if (r.portalUserId != this.currentUserId) {
        const rc: PortalMessageRecipient = {
          id: 0,
          messageId: 0,
          recipientRole: r.recipientRole,
          recipientContact: r.recipientContact,
          recipientInfo: r.recipientInfo,
          portalUserId: r.portalUserId,
          portalUserName: r.portalUserName,
          isReadConfirmationRequired: true,
        };
        recipients.push(rc);
      }
    });

    this.replyMessage(recipients);
  }

  replyMessage(recipients: PortalMessageRecipient[]) {
    let disableRecipientSelection = false;
    let disableTemplates = false;

    if (this.applicationPortalTypeId != ApplicationPortalType.ClaimCenter) {
      disableRecipientSelection = true;
      disableTemplates = true;
    }

    const dialogSettings: CustomDialogSettings = {
      content: ComposePortalMessageComponent,
      appendTo: this.containerRef,
      title: this.translateService.instant(
        "claims-center.claimMessages.composePortalMessage"
      ),
      minHeight: "70vh",
      minWidth: "60vw",
      maxHeight: "95vh",
      maxWidth: "95vw",
      dialogInputs: {
        claimIdentifier: this.message.claimIdentifier,
        threadId: this.message.threadId,
        inputRecipients: recipients,
        applicationPortalTypeId: this.applicationPortalTypeId,
        disableRecipientSelection: disableRecipientSelection,
        disableTemplates: disableTemplates,
        linkedToMessageId: this.message.id,
        initialTitle: this.getReplyTitle(),
      },
      resultCallback: (r: DialogResult) => {
        if (r["text"] == "OK") {
          this.threadChangeEvent.emit("edit");
        }
      },
    };

    this.customDialogService.showDialog(dialogSettings);
  }

  private getReplyTitle(): string {
    const title = this.message.title?.trim();
    if (!title) {
      return "";
    }
    return /^Re:/i.test(title) ? title : `Re: ${title}`;
  }

  getRecipientFromSender(): PortalMessageRecipient {
    return {
      portalUserId: this.message.senderPortalUserId,
      portalUserName: this.message.createdBy,
      id: 0,
      messageId: 0,
      recipientRole: this.message.senderRoleId,
      recipientContact: "",
      recipientInfo: this.message.senderInfo,
      isReadConfirmationRequired: true,
    };
  }

  confirmReading() {
    const confirmationData: ConfirmReadingByRecipientData = {
      recipientId: this.selfRecipient.id,
      messageId: this.message.id,
      portalUserId: null,
    };

    this.messageService.confirmReading(confirmationData).subscribe(() => {
      this.userMessagesIndicationService.refreshData();
      this.uiNotification.showSuccess("validations.dataStoredSuccesfully");
      this.threadChangeEvent.emit("edit");
    });
  }
}
